import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { Card, CardBody, CardHeader, Chip, Button, Tabs, Tab, Textarea, Input } from "@heroui/react";
import { ArrowLeftIcon, PencilIcon, CheckIcon, XMarkIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import moment from "moment";
import Select from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";

const TicketDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isCreateMode = !id || id === "new";

  const [ticketData, setTicketData] = useState<any>(null);
  const [replies, setReplies] = useState<any>([]);
  const [logs, setLogs] = useState<any>([]);
  const [replyMessage, setReplyMessage] = useState<string>("");

  // Email reply states
  const [showEmailReply, setShowEmailReply] = useState<boolean>(false);
  const [emailReplyHtml, setEmailReplyHtml] = useState<string>("");
  const [emailTrail, setEmailTrail] = useState<any[]>([]);
  const [expandedEmails, setExpandedEmails] = useState<any>({});

  // Determine if ticket is email type
  const isEmailType = useMemo(() => ticketData?.mailData != null, [ticketData]);

  // Edit mode states - start in edit mode for create
  const [isEditMode, setIsEditMode] = useState<boolean>(isCreateMode);
  const [editSubject, setEditSubject] = useState<string>("");
  const [editDetails, setEditDetails] = useState<string>("");
  const [editStatus, setEditStatus] = useState<any>(null);
  const [editPriority, setEditPriority] = useState<any>(null);
  const [editCategory, setEditCategory] = useState<any>(null);

  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  const statusOptions = [
    { value: "Open", label: "Open" },
    { value: "In Progress", label: "In Progress" },
    { value: "Closed", label: "Closed" },
  ];

  const priorityOptions = [
    { value: "Low", label: "Low" },
    { value: "Medium", label: "Medium" },
    { value: "High", label: "High" },
    { value: "Urgent", label: "Urgent" },
  ];

  const categoryOptions = [
    { value: "Issue", label: "Issue" },
    { value: "Support", label: "Support" },
    { value: "Pickup", label: "Pickup" },
    { value: "Delivery", label: "Delivery" },
    { value: "SW-Bug", label: "SW-Bug" },
    { value: "SW-Feature", label: "SW-Feature" },
  ];

  // Fetch Ticket Details
  const { refetch: refetchTicket, isLoading: isLoadingTicket } = useQuery(
    ["TicketDetail", id],
    () => ListCommanWay("/admin/ticket/list", { id: Number(id) }, { include: ["_user"] }),
    {
      enabled: !!id && !isCreateMode,
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS" && data.data.data.length > 0) {
          const ticket = data.data.data[0];
          setTicketData(ticket);
          setEditSubject(ticket.subject || "");
          setEditDetails(ticket.details || "");
          setEditStatus(ticket.status ? { value: ticket.status, label: ticket.status } : null);
          setEditPriority(ticket.priority ? { value: ticket.priority, label: ticket.priority } : null);
          setEditCategory(ticket.category ? { value: ticket.category, label: ticket.category } : null);
        }
      },
    }
  );

  // Fetch Ticket Replies
  const { refetch: refetchReplies, isLoading: isLoadingReplies } = useQuery(
    ["TicketReplies", id],
    () =>
      ListCommanWay(
        "/admin/ticketreply/list",
        { ticketId: Number(id), isLog: [null, "", "0"] },
        { paginate: 100, order: [["createdAt", "ASC"]], include: ["_user"] }
      ),
    {
      enabled: !!id && !isCreateMode,
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setReplies(data.data.data || []);
        }
      },
    }
  );

  // Fetch Ticket Logs
  const { refetch: refetchLogs, isLoading: isLoadingLogs } = useQuery(
    ["TicketLogs", id],
    () => ListCommanWay("/admin/ticketreply/list", { ticketId: Number(id), isLog: "1" }, { paginate: 100, order: [["createdAt", "ASC"]] }),
    {
      enabled: !!id && !isCreateMode,
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setLogs(data.data.data || []);
        }
      },
    }
  );

  // Fetch Email Trail via O365 API (same as ViewPickupMail)
  const { isLoading: isLoadingEmailTrail, refetch: refetchEmailTrail } = useQuery(
    ["TicketEmailTrail", ticketData?.ticket_number],
    () =>
      ListCommanWay(
        "/admin/o365/get-pickup-trail",
        { pickup_code: `Ticket Number: ${ticketData?.ticket_number}` },
        {}
      ),
    {
      enabled: isEmailType && !!ticketData?.ticket_number,
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setEmailTrail(data.data.data || []);
        }
      },
    }
  );

  // Add Reply Mutation
  const { mutate: mutateAddReply, isLoading: isAddingReply } = useMutation(
    (data: any) =>
      CreateCommanWay({
        url: "/admin/ticketreply/create",
        data: data,
      }),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          UseToast("Reply Added Successfully", "success");
          setReplyMessage("");
          refetchReplies();
          refetchLogs();
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Email Reply-to-All Mutation via O365
  const { mutate: mutateEmailReply, isLoading: isEmailReplying } = useMutation(
    (data: any) =>
      CreateCommanWay({
        url: "/admin/o365/reply-to-all",
        data: data,
      }),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          UseToast("Email Reply Sent Successfully", "success");
          setEmailReplyHtml("");
          setShowEmailReply(false);
          refetchEmailTrail();
          refetchReplies();
          refetchLogs();
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Update Ticket Mutation
  const { mutate: mutateUpdateTicket, isLoading: isUpdatingTicket } = useMutation(
    (data: any) => UpdateCommanWay(data),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          UseToast("Ticket Updated Successfully", "success");
          setIsEditMode(false);
          refetchTicket();
          refetchLogs();
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Create Ticket Mutation
  const { mutate: mutateCreateTicket, isLoading: isCreatingTicket } = useMutation(
    (data: any) =>
      CreateCommanWay({
        url: "/admin/ticket/create",
        data: data,
      }),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          UseToast("Ticket Created Successfully", "success");
          // Navigate to the new ticket's detail page
          //navigate(`/tickets/detail/${data.data.id}`, { replace: true });
          navigate(`/tickets/open`, { replace: true });
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const handleSubmitReply = () => {
    if (!replyMessage.trim()) {
      UseToast("Please enter a message", "error");
      return;
    }

    mutateAddReply({
      ticketId: Number(id),
      message: replyMessage,
      user: userDetails?.id || userDetails?.userId,
      isLog: "0",
    });
  };

  const handleSubmitEmailReply = () => {
    // Strip HTML tags to check if there's actual content
    const stripped = emailReplyHtml.replace(/<[^>]*>/g, "").trim();
    if (!stripped) {
      UseToast("Please enter a reply message", "error");
      return;
    }

    mutateEmailReply({
      ticketId: Number(id),
      replyContent: emailReplyHtml,
    });
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      // Cancel edit - reset values
      setEditSubject(ticketData.subject || "");
      setEditDetails(ticketData.details || "");
      setEditStatus(ticketData.status ? { value: ticketData.status, label: ticketData.status } : null);
      setEditPriority(ticketData.priority ? { value: ticketData.priority, label: ticketData.priority } : null);
      setEditCategory(ticketData.category ? { value: ticketData.category, label: ticketData.category } : null);
    }
    setIsEditMode(!isEditMode);
  };

  const handleSaveTicket = () => {
    if (!editSubject.trim()) {
      UseToast("Subject is required", "error");
      return;
    }

    const ticketPayload = {
      subject: editSubject,
      details: editDetails,
      status: editStatus?.value || "Open",
      priority: editPriority?.value || "Medium",
      category: editCategory?.value || "",
    };

    if (isCreateMode) {
      mutateCreateTicket(ticketPayload);
    } else {
      mutateUpdateTicket({
        url: `/admin/ticket/update/${id}`,
        data: ticketPayload,
      });
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "danger";
      case "high":
        return "warning";
      case "medium":
        return "primary";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "primary";
      case "in progress":
        return "warning";
      case "closed":
        return "success";
      default:
        return "default";
    }
  };

  // Quill editor modules/formats for email reply
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ color: [] }, { background: [] }],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "color", "background", "link"];

  // Parse email body into new content and quoted content (from ViewPickupMail)
  const parseEmailBody = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || "<div></div>", "text/html");

    const quotedSelectors = ["blockquote", 'div[class*="quote"]'];
    const quotedElements = doc.querySelectorAll(quotedSelectors.join(","));

    let quotedContent = "";
    let newContent = html;

    if (quotedElements.length > 0) {
      quotedContent = Array.from(quotedElements).map((el) => el.outerHTML).join("");
      quotedElements.forEach((el) => el.remove());
      newContent = doc.body.innerHTML || html;
      return { newContent, quotedContent };
    }

    const separators = doc.querySelectorAll('div[id*="divRplyFwdMsg"]');
    if (separators.length > 0) {
      const separator = separators[0];
      const parent = separator.parentNode;
      const nodes = Array.from(parent.childNodes);
      const separatorIndex = nodes.indexOf(separator);
      const newNodes = nodes.slice(0, separatorIndex);
      const quotedNodes = nodes.slice(separatorIndex + 1);
      newContent = newNodes.map((node: any) => node.outerHTML || node.textContent).join("") || "<p></p>";
      quotedContent = quotedNodes.map((node: any) => node.outerHTML || node.textContent).join("") || "";
      return { newContent, quotedContent };
    }

    const textContent = doc.body.textContent || "";
    const quoteMarkers = [/From:.*Sent:.*To:/i, /\n> /, /On .* wrote:/i];
    for (const marker of quoteMarkers) {
      const matchIndex = textContent.search(marker);
      if (matchIndex !== -1) {
        const htmlArray = html.split("\n");
        const textArray = textContent.split("\n");
        let splitLine = 0;
        let textPos = 0;
        for (let i = 0; i < textArray.length; i++) {
          textPos += textArray[i].length + 1;
          if (textPos > matchIndex) { splitLine = i; break; }
        }
        newContent = htmlArray.slice(0, splitLine).join("\n") || "<p></p>";
        quotedContent = htmlArray.slice(splitLine).join("\n") || "";
        return { newContent, quotedContent };
      }
    }
    return { newContent, quotedContent };
  };

  const toggleEmailExpand = (index: number) => {
    setExpandedEmails((prev: any) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <>
      {((isLoadingTicket && !isCreateMode) || isAddingReply || isUpdatingTicket || isCreatingTicket || isEmailReplying) && <Spinner />}

      <div className="px-4 py-2 font-inter">
        {/* Header with Back Button and Edit Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button size="sm" variant="light" startContent={<ArrowLeftIcon className="h-4 w-4" />} onClick={() => navigate(-1)}>
              Back
            </Button>
            <h1 className="text-lg font-bold font-inter">
              {isCreateMode ? "Create New Ticket" : "Ticket Details"}
              {isEmailType && (
                <Chip size="sm" color="secondary" variant="flat" className="ml-2" startContent={<EnvelopeIcon className="h-3 w-3" />}>
                  Email
                </Chip>
              )}
            </h1>
          </div>
          <div className="flex gap-2">
            {isCreateMode ? (
              <Button size="sm" color="success" variant="flat" startContent={<CheckIcon className="h-4 w-4" />} onClick={handleSaveTicket}>
                Create Ticket
              </Button>
            ) : !isEditMode ? (
              <Button size="sm" color="primary" variant="flat" startContent={<PencilIcon className="h-4 w-4" />} onClick={handleEditToggle}>
                Edit
              </Button>
            ) : (
              <>
                <Button size="sm" color="success" variant="flat" startContent={<CheckIcon className="h-4 w-4" />} onClick={handleSaveTicket}>
                  Save
                </Button>
                <Button size="sm" color="danger" variant="flat" startContent={<XMarkIcon className="h-4 w-4" />} onClick={handleEditToggle}>
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>

        {/* ========== EMAIL TYPE LAYOUT (2-column) ========== */}
        {isEmailType && ticketData && (
          <div className="grid grid-cols-3 md:grid-cols-1 gap-4">
            {/* LEFT COLUMN - Ticket Info + Activity */}
            <div className="col-span-1 md:col-span-1 space-y-4">
              {/* Status / Priority / Category */}
              <Card>
                <CardBody className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Status</span>
                    {!isEditMode ? (
                      <Chip color={getStatusColor(ticketData?.status)} size="sm" variant="flat">
                        <span className="font-semibold px-1">{ticketData?.status || "Open"}</span>
                      </Chip>
                    ) : (
                      <div className="min-w-[130px]">
                        <Select options={statusOptions} value={editStatus} onChange={setEditStatus} placeholder="Status" className="text-xs" styles={{ control: (base) => ({ ...base, minHeight: "28px", fontSize: "12px" }) }} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Priority</span>
                    {!isEditMode ? (
                      <Chip color={getPriorityColor(ticketData?.priority)} size="sm" variant="flat">
                        <span className="font-semibold px-1">{ticketData?.priority || "Medium"}</span>
                      </Chip>
                    ) : (
                      <div className="min-w-[130px]">
                        <Select options={priorityOptions} value={editPriority} onChange={setEditPriority} placeholder="Priority" className="text-xs" styles={{ control: (base) => ({ ...base, minHeight: "28px", fontSize: "12px" }) }} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Category</span>
                    {!isEditMode ? (
                      ticketData?.category ? (
                        <Chip size="sm" variant="flat" color="secondary">
                          <span className="font-semibold px-1">{ticketData.category}</span>
                        </Chip>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )
                    ) : (
                      <div className="min-w-[130px]">
                        <Select options={categoryOptions} value={editCategory} onChange={setEditCategory} placeholder="Category" className="text-xs" isClearable styles={{ control: (base) => ({ ...base, minHeight: "28px", fontSize: "12px" }) }} />
                      </div>
                    )}
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Created</span>
                      <span className="text-xs font-medium text-gray-900">{ticketData?.createdAt}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500">Updated</span>
                      <span className="text-xs font-medium text-gray-900">{ticketData?.updatedAt || "-"}</span>
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Title & Ticket No */}
              <Card>
                <CardBody className="p-4">
                  <h2 className="text-base font-bold text-gray-900 mb-1">{ticketData?.subject || "No Subject"}</h2>
                  <p className="text-sm text-gray-500">Ticket #{ticketData?.ticket_number || ticketData?.id}</p>
                </CardBody>
              </Card>

              {/* Assigned To */}
              <Card>
                <CardBody className="p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Assigned To</h3>
                  <span className="text-sm text-gray-900 font-medium">{ticketData?._user?.name || "Unassigned"}</span>
                </CardBody>
              </Card>

              {/* Raised By */}
              {(ticketData?.fromEmail || ticketData?.fromContact) && (
                <Card>
                  <CardBody className="p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Raised By</h3>
                    {ticketData?.fromEmail && (
                      <div className="flex items-start gap-2">
                        <span className="text-xs text-gray-500 min-w-[50px]">Email:</span>
                        <span className="text-sm text-gray-900 font-medium">{ticketData.fromEmail}</span>
                      </div>
                    )}
                    {ticketData?.fromContact && (
                      <div className="flex items-start gap-2 mt-1">
                        <span className="text-xs text-gray-500 min-w-[50px]">Contact:</span>
                        <span className="text-sm text-gray-900 font-medium">{ticketData.fromContact}</span>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}

              {/* Activity Log */}
              <Card>
                <CardBody className="p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Activity Log ({logs.length})</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {isLoadingLogs ? (
                      <Spinner />
                    ) : logs.length === 0 ? (
                      <p className="text-center text-gray-500 text-sm py-2">No activity logs</p>
                    ) : (
                      logs.map((log: any) => (
                        <div key={log.id} className="bg-blue-50 border-l-4 border-blue-500 p-2 rounded-r">
                          <p className="font-semibold text-xs text-gray-900">{log.user || "System"}</p>
                          <p className="text-xs text-gray-500">{log.createdAt || "-"}</p>
                          <p className="text-xs text-gray-700 mt-1">{log.message || "-"}</p>
                        </div>
                      ))
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* RIGHT COLUMN - Email Conversation + Reply */}
            <div className="col-span-2 md:col-span-1">
              <Card className="h-full">
                <CardBody className="p-4 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <EnvelopeIcon className="h-4 w-4" /> Email Conversation
                    </h3>
                    <Button
                      color="primary"
                      variant="solid"
                      size="sm"
                      onClick={() => setShowEmailReply(true)}
                      startContent={<EnvelopeIcon className="h-4 w-4" />}
                    >
                      Reply
                    </Button>
                  </div>

                  {/* Email Thread */}
                  <div className="flex-1 space-y-4 mb-4">
                    {isLoadingEmailTrail ? (
                      <div className="text-center py-4"><Spinner /></div>
                    ) : emailTrail.length === 0 ? (
                      <p className="text-center text-gray-500 text-sm py-4">No email data available</p>
                    ) : (
                      emailTrail.map((email: any, index: number) => {
                        const { newContent, quotedContent } = parseEmailBody(email.body);
                        return (
                          <div key={index} className={`border rounded-lg ${index % 2 === 0 ? 'bg-white' : 'bg-white'}`}>
                            {/* Email Header */}
                            <div className={`px-4 py-2 rounded-t-lg border-b ${index % 2 === 0 ? 'bg-green-200 border-gray-200' : 'bg-gray-200 border-gray-300'}`}>
                              <div className="flex justify-between items-start">
                                <div className="space-y-0.5">
                                  {email.from && (
                                    <p className="text-sm font-semibold text-gray-900">From: <span className="font-normal">{email.from}</span></p>
                                  )}
                                  {email.ccRecipients?.length > 0 && (
                                    <p className="text-xs text-gray-600">CC: {email.ccRecipients.join(", ")}</p>
                                  )}
                                  {email.replyTo?.length > 0 && (
                                    <p className="text-xs text-gray-600">Reply to: {email.replyTo.join(", ")}</p>
                                  )}
                                </div>
                                {email.receivedDateTime && (
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {moment(email.receivedDateTime).isValid() ? moment(email.receivedDateTime).format("DD-MM-YYYY hh:mm A") : email.receivedDateTime}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Email Body */}
                            <div className="px-4 py-3 text-sm text-gray-700 email-html-content">
                              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newContent) }} />
                              {quotedContent && (
                                <div>
                                  {expandedEmails[index] ? (
                                    <>
                                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quotedContent) }} />
                                      <button className="text-blue-500 text-sm mt-2" onClick={() => toggleEmailExpand(index)}>Collapse quoted text</button>
                                    </>
                                  ) : (
                                    <button className="text-blue-500 text-sm mt-2" onClick={() => toggleEmailExpand(index)}>... Show quoted text</button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}

                    {/* Show replies below email thread */}
                    {replies.length > 0 && (
                      <>
                        <div className="border-t border-gray-300 pt-3">
                          <p className="text-xs font-semibold text-gray-500 mb-2">Replies</p>
                        </div>
                        {replies.map((reply: any) => (
                          <div key={reply.id} className="border border-gray-200 rounded-lg">
                            <div className="bg-blue-50 px-4 py-2 rounded-t-lg border-b border-gray-200">
                              <div className="flex justify-between items-start">
                                <p className="text-sm font-semibold text-gray-900">{reply._user?.name || "Staff"}</p>
                                <span className="text-xs text-gray-500">{reply.createdAt || "-"}</span>
                              </div>
                            </div>
                            <div
                              className="px-4 py-3 text-sm text-gray-700 email-html-content"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reply.message || "") }}
                            />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        )}

        {/* Fixed bottom email reply editor */}
        {isEmailType && showEmailReply && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-300 shadow-2xl p-4">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <EnvelopeIcon className="h-4 w-4" /> Reply
                </h4>
                <Button
                  size="sm"
                  color="danger"
                  variant="flat"
                  onClick={() => {
                    setShowEmailReply(false);
                    setEmailReplyHtml("");
                  }}
                  startContent={<XMarkIcon className="h-4 w-4" />}
                >
                  Close
                </Button>
              </div>
              <ReactQuill
                theme="snow"
                value={emailReplyHtml}
                onChange={setEmailReplyHtml}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Type your reply..."
                style={{ height: "200px", marginBottom: "50px" }}
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button size="sm" color="primary" variant="solid" onClick={handleSubmitEmailReply} isLoading={isEmailReplying}>
                  Send Reply
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ========== REGULAR TYPE LAYOUT (existing) ========== */}
        {!isEmailType && (
        <>
        {/* Ticket Information Card */}
        {(ticketData || isCreateMode) && (
          <Card className="mb-4">
            <CardBody className="p-4">
              {/* Top Line - Status, Priority, Category, Dates */}
              <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Status:</span>
                  {!isEditMode && !isCreateMode ? (
                    <Chip color={getStatusColor(ticketData?.status)} size="sm" variant="flat">
                      <span className="font-semibold px-1">{ticketData?.status || "Open"}</span>
                    </Chip>
                  ) : (
                    <div className="min-w-[120px]">
                      <Select
                        options={statusOptions}
                        value={editStatus}
                        onChange={setEditStatus}
                        placeholder="Status"
                        className="text-xs z-30"
                        styles={{ control: (base) => ({ ...base, minHeight: "28px", fontSize: "12px" }) }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Priority:</span>
                  {!isEditMode && !isCreateMode ? (
                    <Chip color={getPriorityColor(ticketData?.priority)} size="sm" variant="flat">
                      <span className="font-semibold px-1">{ticketData?.priority || "Medium"}</span>
                    </Chip>
                  ) : (
                    <div className="min-w-[120px]">
                      <Select
                        options={priorityOptions}
                        value={editPriority}
                        onChange={setEditPriority}
                        placeholder="Priority"
                        className="text-xs z-20"
                        styles={{ control: (base) => ({ ...base, minHeight: "28px", fontSize: "12px" }) }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Category:</span>
                  {!isEditMode && !isCreateMode ? (
                    ticketData?.category ? (
                      <Chip size={"sm"} variant="flat" color="secondary">
                        <span className="font-semibold px-1">{ticketData.category}</span>
                      </Chip>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )
                  ) : (
                    <div className="min-w-[120px]">
                      <Select
                        options={categoryOptions}
                        value={editCategory}
                        onChange={setEditCategory}
                        placeholder="Category"
                        className="text-xs z-10"
                        isClearable
                        styles={{ control: (base) => ({ ...base, minHeight: "28px", fontSize: "12px" }) }}
                      />
                    </div>
                  )}
                </div>
                {!isCreateMode && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Created:</span>
                      <span className="text-xs font-medium text-gray-900">
                        {ticketData?.createdAt }
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Last Updated:</span>
                      <span className="text-xs font-medium text-gray-900">
                        {ticketData?.updatedAt || "-"}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Title Section */}
              <div className="mb-4">
                {isEditMode || isCreateMode ? (
                  <>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Subject *</label>
                    <input
                      type="text"
                      value={editSubject}
                      onChange={(e) => setEditSubject(e.target.value)}
                      className="text-gray-900 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter ticket subject"
                    />
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">{ticketData?.subject || "No Subject"}</h2>
                    <p className="text-sm text-gray-500">Ticket #{ticketData?.ticket_number || ticketData?.id}</p>
                  </>
                )}
              </div>

              {/* Details Section */}
              {(ticketData?.details || isEditMode || isCreateMode) && (
                <div className="mb-4 pb-4 border-b">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Details</h3>
                  {isEditMode || isCreateMode ? (
                    <textarea
                      value={editDetails}
                      onChange={(e) => setEditDetails(e.target.value)}
                      className="text-sm text-gray-700 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                      placeholder="Enter ticket details (optional)"
                    />
                  ) : (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticketData?.details}</p>
                  )}
                </div>
              )}

              {/* Ticket Raised By Section - Hide in create mode */}
              {!isCreateMode && (
                <div className="grid grid-cols-2 md:grid-cols-1 gap-4 bg-gray-50 p-3 rounded-lg">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Raised By</h3>
                    <div className="space-y-1">
                      {ticketData?.fromEmail && (
                        <div className="flex items-start gap-2">
                          <span className="text-xs text-gray-500 min-w-[60px]">Email:</span>
                          <span className="text-sm text-gray-900 font-medium">{ticketData.fromEmail}</span>
                        </div>
                      )}
                      {ticketData?.fromContact && (
                        <div className="flex items-start gap-2">
                          <span className="text-xs text-gray-500 min-w-[60px]">Contact:</span>
                          <span className="text-sm text-gray-900 font-medium">{ticketData.fromContact}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Assigned To</h3>
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-gray-900 font-medium">{ticketData?._user?.name || "Unassigned"}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Tabs for Replies and Logs - Hide in create mode */}
        {!isCreateMode && (
        <Card>
          <CardBody className="p-0">
            <Tabs aria-label="Ticket tabs" className="w-full" classNames={{ tabList: "w-full" }}>
              {/* Replies Tab */}
              <Tab key="replies" title={`Replies (${replies.length})`}>
                <div className="p-4">
                  {/* Reply Form */}
                  <div className="mb-4 [&_textarea]:border-none [&_textarea]:px-0">
                    <Textarea
                      label="Add Reply"
                      labelPlacement="outside"
                      variant="bordered"
                      value={replyMessage}
                      onValueChange={setReplyMessage}
                      placeholder="Type your reply here..."
                      minRows={3}
                      size="sm"
                    />
                    <div className="flex justify-end mt-2">
                      <Button color="primary" size="sm" onClick={handleSubmitReply} isLoading={isAddingReply}>
                        Send Reply
                      </Button>
                    </div>
                  </div>

                  {/* Replies List */}
                  <div className="space-y-3">
                    {isLoadingReplies ? (
                      <div className="text-center py-4">
                        <Spinner />
                      </div>
                    ) : replies.length === 0 ? (
                      <div className="text-center text-gray-500 py-4">No replies yet</div>
                    ) : (
                      replies.map((reply: any) => (
                        <Card key={reply.id} className="bg-gray-50">
                          <CardBody className="p-3">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-sm text-gray-900">{reply._user?.name || "User"}</p>
                                <p className="text-xs text-gray-500">
                                  {reply.createdAt || "-"}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{reply.message || "-"}</p>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              </Tab>

              {/* Logs Tab */}
              <Tab key="logs" title={`Activity Log (${logs.length})`}>
                <div className="p-4">
                  <div className="space-y-3">
                    {isLoadingLogs ? (
                      <div className="text-center py-4">
                        <Spinner />
                      </div>
                    ) : logs.length === 0 ? (
                      <div className="text-center text-gray-500 py-4">No activity logs</div>
                    ) : (
                      logs.map((log: any) => (
                        <Card key={log.id} className="bg-blue-50 border-l-4 border-blue-500">
                          <CardBody className="p-3">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-sm text-gray-900">{log.user || "System"}</p>
                                <p className="text-xs text-gray-500">
                                  {log.createdAt || "-"}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{log.message || "-"}</p>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              </Tab>
            </Tabs>
          </CardBody>
        </Card>
        )}
        </>
        )}
      </div>
    </>
  );
};

export default TicketDetail;
